import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

export const FOCUSABLE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]';

/**
 * Whether an element is a real tab stop: not opted out via tabindex, not disabled and
 * rendered. Per the HTML Standard `offsetParent` is null both for elements inside a
 * `display: none` subtree and for `position: fixed` elements, so the latter fall back to
 * having a box at all.
 */
export function isReachable(element: HTMLElement): boolean {
  if (element.getAttribute('tabindex') === '-1') return false;
  if (element.matches(':disabled')) return false;
  if (element.offsetParent !== null) return true;
  return getComputedStyle(element).position === 'fixed' && element.getClientRects().length > 0;
}

/**
 * Tab trap for drawers and panels: keeps Tab/Shift+Tab cycling through `trapped`
 * (in DOM order) and recovers focus into the cycle when it is outside of it.
 * Call it from a `keydown` listener once the event is known to be a Tab press.
 */
export function handleTrappedTabKey(event: KeyboardEvent, trapped: HTMLElement[]): void {
  if (trapped.length === 0) return;

  const first = trapped[0];
  const last = trapped.at(-1);
  const active = document.activeElement;

  if (!(active instanceof HTMLElement) || !trapped.includes(active)) {
    event.preventDefault();
    first.focus();
    return;
  }

  if (event.shiftKey && active === first) {
    event.preventDefault();
    last?.focus();
    return;
  }

  if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

/**
 * Keyboard navigation for a `role="tablist"`: arrow keys move focus between `[role="tab"]` elements.
 * @param event - Keyboard event from the tablist container.
 */
export function handleTabListKeyDown(event: ReactKeyboardEvent<HTMLElement>): void {
  const tabs = event.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]');
  if (!tabs.length) return;

  const activeIndex = Array.from(tabs).indexOf(document.activeElement as HTMLElement);
  if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
    event.preventDefault();
    tabs[(activeIndex + 1) % tabs.length].focus();
  } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
    event.preventDefault();
    tabs[(activeIndex - 1 + tabs.length) % tabs.length].focus();
  }
}
